/* Version: 3.5.155 */
'use client';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import 'react';
import PropTypes from 'prop-types';
import i18n from '../../lang/i18n/index.js';
import { isIE } from '../../utils/platforms.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import Pillar from './assets/Pillar.js';
import Circle from './Circle.js';
import CircularIndeterminate from './CircularIndeterminate.js';
import { PROGRESS_VARIANT } from './constants.js';
import { Loader, LoaderElement, ProgressWrapper, LoaderMessage } from './styledComponents.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useTheme from '../../hooks/useTheme.js';
import useLang from '../../hooks/useLang.js';

var Progress = function Progress(_ref) {
  var controlledId = _ref.id,
    _ref$doShow = _ref.doShow,
    doShow = _ref$doShow === undefined ? true : _ref$doShow,
    message = _ref.message,
    _ref$size = _ref.size,
    size = _ref$size === undefined ? 50 : _ref$size,
    _ref$magnitude = _ref.magnitude,
    magnitude = _ref$magnitude === undefined ? 4 : _ref$magnitude,
    color = _ref.color,
    _ref$variant = _ref.variant,
    variant = _ref$variant === undefined ? PROGRESS_VARIANT.CIRCLE_INDETERMINATE : _ref$variant,
    _ref$isFloating = _ref.isFloating,
    isFloating = _ref$isFloating === undefined ? true : _ref$isFloating,
    _ref$customStyle = _ref.customStyle,
    customStyle = _ref$customStyle === undefined ? {
      spinnerStyle: {},
      messageStyle: {}
    } : _ref$customStyle;
  var _useDerivedState = useDerivedState(useId('progress-'), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var theme = useTheme();
  var lang = useLang();
  var _safeCustomStyle = safeCustomStyle(customStyle),
    spinnerStyle = _safeCustomStyle.spinnerStyle,
    messageStyle = _safeCustomStyle.messageStyle;
  var displayMessage = function displayMessage(loaderMessage) {
    return loaderMessage && /*#__PURE__*/jsx(LoaderMessage, {
      "data-testid": "progress-message",
      variant: variant,
      diameter: size,
      theme: theme,
      messageStyle: messageStyle,
      children: loaderMessage
    });
  };
  var Container;
  if (variant === PROGRESS_VARIANT.STYLIZED_M) {
    Container = /*#__PURE__*/jsxs("div", {
      "data-testid": "progress-stylized-m",
      children: [/*#__PURE__*/jsxs(Loader, {
        variant: variant,
        children: [/*#__PURE__*/jsx(LoaderElement, {
          size: size,
          children: /*#__PURE__*/jsx(Pillar, {
            id: "".concat(id, "-first-pillar")
          })
        }), /*#__PURE__*/jsx(LoaderElement, {
          size: size,
          children: /*#__PURE__*/jsx(Pillar, {
            id: "".concat(id, "-second-pillar")
          })
        }), /*#__PURE__*/jsx(LoaderElement, {
          size: size,
          children: /*#__PURE__*/jsx(Pillar, {
            id: "".concat(id, "-third-pillar")
          })
        })]
      }), displayMessage(message || i18n[lang].progressBar.loading)]
    });
  } else if (variant === PROGRESS_VARIANT.IE_FALLBACK || isIE()) {
    Container = /*#__PURE__*/jsxs("div", {
      "data-testid": "progress-fallback",
      children: [/*#__PURE__*/jsx(Loader, {
        variant: variant,
        diameter: size,
        children: /*#__PURE__*/jsx(Circle, {
          diameter: size,
          color: color
        })
      }), displayMessage(message)]
    });
  } else {
    Container = /*#__PURE__*/jsxs("div", {
      "data-testid": "progress-circularIndeterminate",
      children: [/*#__PURE__*/jsx(Loader, {
        variant: variant,
        diameter: size,
        children: /*#__PURE__*/jsx(CircularIndeterminate, {
          id: id,
          magnitude: magnitude,
          color: color
        })
      }), displayMessage(message)]
    });
  }
  if (doShow) {
    return /*#__PURE__*/jsx(ProgressWrapper, {
      id: id,
      diameter: size,
      variant: variant,
      isFloating: isFloating,
      "data-testid": "progress",
      spinnerStyle: spinnerStyle,
      children: Container
    });
  }
  return null;
};
process.env.NODE_ENV !== "production" ? Progress.propTypes = {
  /** DOM elmement ID */
  id: PropTypes.string,
  /** Flag to indicate whether operation is in progress (i.e. show spinner) */
  doShow: PropTypes.bool,
  /** Spinner color override */
  color: PropTypes.string,
  /** Message to show under the loader (applies only to STYLIZED_M variant) */
  message: PropTypes.string,
  /** Diameter in px of circular progress indicator (spinner) */
  size: PropTypes.number,
  /** Spinner thickness, for animated svg spinner */
  magnitude: PropTypes.number,
  /** Indicates whether spinner is positioned absolutey above items and centered */
  isFloating: PropTypes.bool,
  /** Progress indicator variant */
  variant: PropTypes.oneOf([PROGRESS_VARIANT.CIRCLE_INDETERMINATE, PROGRESS_VARIANT.IE_FALLBACK, PROGRESS_VARIANT.STYLIZED_M]),
  /** Custom style object */
  customStyle: PropTypes.shape({
    spinnerStyle: PropTypes.object,
    messageStyle: PropTypes.object
  })
} : undefined;
process.env.NODE_ENV !== "production" ? Progress.displayName = 'Progress' : undefined;

export { Progress as default };
